import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { PiCalendarBlank, PiCaretLeft } from 'react-icons/pi'

import { Alert } from '../../components/FbUI/Alert'
import { DatePicker } from '../../components/FormUtils/DatePicker'
import TimePicker from '../../components/FormUtils/TimePicker'
import { FbModal as Modal } from '../../components/UI/Modal'
import { getApiErrorDetail } from '../../utils/getApiErrorDetail'
import type { ReplyRecipient } from './replies_api'
import { useScheduleReply } from './replies_api'
import { zonedDateTimeToUtcIso } from './scheduleTime'

// Mirror the backend's MIN_LEAD_SECONDS in ScheduleReplyUsecase (server/emailbison/usecases/schedule_reply.py)
const MIN_LEAD_MS = 60_000

interface SchedulePreset {
  key: string
  label: string
  /** UTC ISO the reply will send at. */
  iso: string
  /** Local date/time shown on the right of the row (e.g. "Jul 17, 8:00 AM"). */
  displayLabel: string
}

const pad = (n: number): string => String(n).padStart(2, '0')
const ymdLocal = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

/**
 * Gmail-style quick presets computed relative to `now` in the operator's local
 * timezone: tomorrow morning (08:00), tomorrow afternoon (13:00), and the next
 * Monday morning (08:00). Each carries the UTC ISO to schedule at plus a local
 * display label for the row.
 */
function buildPresets(now: Date, timeZone: string): SchedulePreset[] {
  const tomorrow = new Date(now)
  tomorrow.setDate(now.getDate() + 1)

  // Next Monday strictly in the future (1..7 days out, never today).
  const nextMonday = new Date(now)
  nextMonday.setDate(now.getDate() + ((8 - now.getDay()) % 7 || 7))
  // If that Monday is tomorrow (today is Sunday), push a week so the three
  // presets stay distinct.
  if (ymdLocal(nextMonday) === ymdLocal(tomorrow)) {
    nextMonday.setDate(nextMonday.getDate() + 7)
  }

  const make = (d: Date, time: string, label: string): SchedulePreset => {
    const iso = zonedDateTimeToUtcIso(ymdLocal(d), time, timeZone)
    return {
      key: label,
      label,
      iso,
      displayLabel: new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }),
    }
  }

  return [
    make(tomorrow, '08:00', 'Tomorrow morning'),
    make(tomorrow, '13:00', 'Tomorrow afternoon'),
    make(nextMonday, '08:00', 'Monday morning'),
  ]
}

interface Props {
  show: boolean
  replyId: number
  buildMessage: () => {
    message: string
    cc_emails?: ReplyRecipient[]
    bcc_emails?: ReplyRecipient[]
  } | null
  onScheduled: () => void
  onHide: () => void
}

export function ScheduleReplyModal({
  show,
  replyId,
  buildMessage,
  onScheduled,
  onHide,
}: Props) {
  const timeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  )
  // `pickMode` swaps the preset list for the manual date/time picker.
  const [pickMode, setPickMode] = useState(false)
  const [date, setDate] = useState<string>('')
  const [time, setTime] = useState<string>('09:00')

  const scheduleMutation = useScheduleReply()

  // Recompute presets each time the modal opens so relative dates stay fresh.
  const presets = useMemo(
    () => (show ? buildPresets(new Date(), timeZone) : []),
    [show, timeZone]
  )

  const scheduledIso = useMemo(() => {
    if (!date || !time) return null
    try {
      return zonedDateTimeToUtcIso(date, time, timeZone)
    } catch {
      return null
    }
  }, [date, time, timeZone])

  const isInFuture = useMemo(() => {
    if (!scheduledIso) return false
    return new Date(scheduledIso).getTime() > Date.now() + MIN_LEAD_MS
  }, [scheduledIso])

  const canSubmit = Boolean(date && time && isInFuture)

  async function scheduleAt(iso: string) {
    const built = buildMessage()
    if (!built) {
      toast.error('Write a reply before scheduling.')
      return
    }
    try {
      await scheduleMutation.mutateAsync({
        replyId,
        payload: {
          message: built.message,
          send_at: iso,
          cc_emails: built.cc_emails,
          bcc_emails: built.bcc_emails,
        },
      })
      toast.success('Reply scheduled')
      onScheduled()
      setPickMode(false)
      onHide()
    } catch (err: unknown) {
      toast.error(getApiErrorDetail(err, 'Failed to schedule reply'))
    }
  }

  return (
    <Modal
      open={show}
      onOpenChange={(open: boolean) => {
        if (!open) {
          setPickMode(false)
          onHide()
        }
      }}
      title="Schedule send"
      description={
        <>
          Times shown in <strong>{timeZone}</strong> (your local time).
        </>
      }
      // Only the manual-picker view uses the footer Schedule button; presets
      // schedule on click.
      onAccept={
        pickMode
          ? () => {
              if (scheduledIso) void scheduleAt(scheduledIso)
            }
          : undefined
      }
      acceptButtonText="Schedule"
      blockAccept={!canSubmit}
      loading={scheduleMutation.isPending}
    >
      {pickMode ? (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setPickMode(false)}
            className="flex items-center gap-1 text-sm text-fg-secondary transition-colors hover:text-fg"
          >
            <PiCaretLeft className="size-4" />
            Back
          </button>
          <div>
            <label className="mb-1 block text-xs font-medium text-fg">
              Date
            </label>
            <DatePicker
              value={date}
              onChange={(v) => setDate(v ?? '')}
              allowTyping
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-fg">
              Time
            </label>
            <TimePicker value={time} onChange={setTime} />
          </div>
          {date && time && !isInFuture && (
            <Alert
              color="danger"
              description="Scheduled time must be in the future."
            />
          )}
        </div>
      ) : (
        <div className="flex flex-col">
          {presets.map((preset) => (
            <button
              key={preset.key}
              type="button"
              onClick={() => void scheduleAt(preset.iso)}
              disabled={scheduleMutation.isPending}
              className="flex items-center justify-between gap-4 rounded-md px-3 py-2.5 text-left text-sm transition-colors hover:bg-surface-hover disabled:opacity-50"
            >
              <span className="text-fg">{preset.label}</span>
              <span className="text-fg-secondary">{preset.displayLabel}</span>
            </button>
          ))}
          <div className="my-1 border-t border-line" />
          <button
            type="button"
            onClick={() => setPickMode(true)}
            className="flex items-center gap-2 rounded-md px-3 py-2.5 text-left text-sm text-fg transition-colors hover:bg-surface-hover"
          >
            <PiCalendarBlank className="size-4 text-fg-secondary" />
            Pick date &amp; time
          </button>
        </div>
      )}
    </Modal>
  )
}
